import React from "react";
import { Box, Typography } from "@mui/material";
import { tallyTokens } from "../../theme.tsx";

interface DrawerNavRowProps {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  /** Marks this row as the destination currently on screen. */
  active?: boolean;
  /** Rendered at the end of the row, e.g. a count. */
  trailing?: React.ReactNode;
}

/**
 * A single navigation row in the conversation drawer.
 *
 * Shared by every drawer destination so the icon slot, label, hover and active
 * treatment stay identical. Active rows get a tinted fill plus a rail that sits
 * flush against the drawer edge, bleeding out through the 14px gutter.
 */
const DrawerNavRow: React.FC<DrawerNavRowProps> = ({
  icon,
  label,
  onClick,
  active = false,
  trailing,
}) => (
  <Typography
    variant="h4"
    component="button"
    type="button"
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    sx={{
      // Reset the native button so it looks like the rest of the drawer.
      border: 0,
      textAlign: "left",
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontSize: "0.9rem !important",
      fontWeight: active ? 600 : 500,
      color: active ? tallyTokens.brandInk : tallyTokens.ink2,
      backgroundColor: active ? tallyTokens.brandWash : "transparent",
      width: "100%",
      lineHeight: 1.55,
      margin: 0,
      px: "10px",
      py: "8px",
      borderRadius: `${tallyTokens.radius.md}px`,
      transition: "background-color 140ms ease, color 140ms ease",
      "&:focus-visible": {
        outline: `2px solid ${tallyTokens.brand}`,
        outlineOffset: "-2px",
      },
      "&:hover": {
        backgroundColor: active ? tallyTokens.brandWash : tallyTokens.surface3,
        color: active ? tallyTokens.brandInk : tallyTokens.ink,
      },
      ...(active && {
        "&::before": {
          content: '""',
          position: "absolute",
          left: "-14px",
          top: "7px",
          bottom: "7px",
          width: "3px",
          borderRadius: "0 3px 3px 0",
          backgroundColor: tallyTokens.brand,
        },
      }),
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "18px",
        height: "18px",
      }}
    >
      {icon}
    </Box>
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "left", minWidth: 0 }}
    >
      {label}
    </Box>
    {trailing !== undefined && (
      <Box sx={{ ml: "auto", display: "inline-flex", alignItems: "center" }}>
        {trailing}
      </Box>
    )}
  </Typography>
);

export default DrawerNavRow;
